/**
 * Reusable photo attachment component.
 * - Camera capture or gallery pick (mobile-friendly via <input capture>)
 * - Client-side compression to ~200–400 KB (JPEG, max 1600px on longest side, quality 0.75)
 * - Uploads to /api/photos with polymorphic parent (parent_type, parent_id, site_id, tag)
 * - Thumbnail strip → tap to open in lightbox
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, Loader2, Trash2, X } from "lucide-react";
import { api, apiError, fmtDateTime, API } from "../lib/api";
import { toast } from "sonner";

async function compressImage(file, { maxSide = 1600, quality = 0.75 } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            img.onload = () => {
                let { width, height } = img;
                if (width > height && width > maxSide) {
                    height = Math.round((height * maxSide) / width);
                    width = maxSide;
                } else if (height >= width && height > maxSide) {
                    width = Math.round((width * maxSide) / height);
                    height = maxSide;
                }
                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (!blob) return reject(new Error("compression failed"));
                        resolve(new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
                    },
                    "image/jpeg",
                    quality
                );
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
}

export default function PhotoAttach({
    parentType,
    parentId,
    siteId,
    tag = null,
    disabled = false,
    canDelete = true,
    compact = false,
    onChange,
    testIdPrefix = "photo",
}) {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [lightbox, setLightbox] = useState(null); // index
    const cameraRef = useRef(null);
    const galleryRef = useRef(null);

    const load = useCallback(async () => {
        if (!parentId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/photos", { params: { parent_type: parentType, parent_id: parentId } });
            setPhotos(data);
            onChange && onChange(data);
        } catch (e) {
            // silent
        } finally { setLoading(false); }
    }, [parentType, parentId, onChange]);

    useEffect(() => { load(); }, [load]);

    async function handleFiles(fileList) {
        if (disabled) return;
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const raw of files) {
                if (!raw.type.startsWith("image/")) continue;
                let f = raw;
                try {
                    f = await compressImage(raw);
                } catch { /* upload original */ }
                const fd = new FormData();
                fd.append("file", f);
                fd.append("parent_type", parentType);
                fd.append("parent_id", parentId);
                fd.append("site_id", siteId);
                if (tag) fd.append("tag", tag);
                await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} attached`);
            await load();
        } catch (e) {
            toast.error(apiError(e, "Photo upload failed"));
        } finally { setUploading(false); }
    }

    async function removePhoto(id) {
        try {
            await api.delete(`/photos/${id}`);
            toast.success("Photo removed");
            setLightbox(null);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    function photoUrl(id) {
        return `${API}/photos/${id}/blob`;
    }

    return (
        <div className="mt-2" data-testid={`${testIdPrefix}-attach`}>
            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testIdPrefix}-camera-input`}
            />
            <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
                data-testid={`${testIdPrefix}-gallery-input`}
            />

            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testIdPrefix}-thumb-strip`}>
                    {photos.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setLightbox(i)}
                            className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden border border-gray-200 hover:border-[#1E3355] transition-colors"
                            data-testid={`${testIdPrefix}-thumb-${p.id}`}
                        >
                            <AuthImg src={photoUrl(p.id)} alt={p.tag || p.filename} className="w-full h-full object-cover" />
                            {p.tag && (
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] font-semibold text-center py-0.5 uppercase tracking-wider">{p.tag}</div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {!disabled && (
                <div className={compact ? "flex gap-1.5" : "flex gap-2"}>
                    <button
                        type="button"
                        onClick={() => cameraRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white border border-gray-200 text-[#1E3355] hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-take-photo`}
                    >
                        <Camera className="w-3.5 h-3.5" /> {compact ? "" : "Take photo"}
                    </button>
                    <button
                        type="button"
                        onClick={() => galleryRef.current?.click()}
                        disabled={uploading}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                        data-testid={`${testIdPrefix}-pick-photo`}
                    >
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {compact ? "" : "Attach"}
                    </button>
                </div>
            )}

            {lightbox !== null && photos[lightbox] && (
                <Lightbox
                    photo={photos[lightbox]}
                    photoUrl={photoUrl(photos[lightbox].id)}
                    onClose={() => setLightbox(null)}
                    onPrev={() => setLightbox((i) => (i > 0 ? i - 1 : photos.length - 1))}
                    onNext={() => setLightbox((i) => (i < photos.length - 1 ? i + 1 : 0))}
                    onDelete={canDelete && !disabled ? () => removePhoto(photos[lightbox].id) : null}
                />
            )}
        </div>
    );
}

function AuthImg({ src, alt, className }) {
    const [dataUrl, setDataUrl] = useState(null);
    useEffect(() => {
        let revoked = null;
        (async () => {
            try {
                const token = localStorage.getItem("fx_token");
                const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                revoked = url;
                setDataUrl(url);
            } catch { /* noop */ }
        })();
        return () => { if (revoked) URL.revokeObjectURL(revoked); };
    }, [src]);
    if (!dataUrl) return <div className={className + " bg-gray-100 animate-pulse"} />;
    return <img src={dataUrl} alt={alt} className={className} />;
}

function Lightbox({ photo, photoUrl, onClose, onPrev, onNext, onDelete }) {
    return (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" data-testid="photo-lightbox">
            <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={onClose} data-testid="lightbox-close">
                <X className="w-5 h-5" />
            </button>
            <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 text-2xl" onClick={onPrev}>‹</button>
            <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-2 text-2xl" onClick={onNext}>›</button>
            <div className="w-full h-full flex flex-col items-center justify-center p-6 sm:p-12">
                <AuthImg src={photoUrl} alt={photo.filename} className="max-w-full max-h-[80vh] object-contain rounded-md" />
                <div className="mt-4 text-white/80 text-sm text-center max-w-md">
                    <div className="font-medium">{photo.tag ? photo.tag.toUpperCase() + " · " : ""}{photo.filename}</div>
                    <div className="text-xs text-white/60 mt-1">{fmtDateTime(photo.created_at)} · by {photo.uploader_name} · {(photo.size_bytes / 1024).toFixed(0)} KB</div>
                </div>
                {onDelete && (
                    <button
                        onClick={onDelete}
                        className="mt-4 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-red-500/20 border border-red-400/30 text-red-200 hover:bg-red-500/30"
                        data-testid="lightbox-delete"
                    >
                        <Trash2 className="w-3.5 h-3.5" /> Delete photo
                    </button>
                )}
            </div>
        </div>
    );
}
